import { CurrencyCode, CurrencySymbol, currencies } from '@pubstack/common/src/currency'
import { AdminScope } from '@pubstack/common/src/scope'
import { compareNames } from '@pubstack/common/src/sort'
import { TIMEZONES } from '@pubstack/common/src/timezone'
import { cn } from '@pubstack/ui/utils/cn'
import { AdminCSVUser } from '@pubstack/common/src/user'
import { ChangeEventHandler, FunctionComponent, useCallback, useMemo, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useAdminScopes, useArchiveScope, useCreateScope, useDeleteScope, useUnarchiveScope, useUpdateScope } from '~/api/admin/admin-scope.api'
import { useUploadMarketplaceSellers } from '~/api/admin/marketplace.api'
import { useAdminUsersCSV } from '~/api/admin/users.api'
import { api } from '~/api/HttpProvider'
import { Colors } from '~/assets/style/colors'
import { Sizes } from '~/assets/style/tokens'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import Button from '~/components/Button'
import { ConfirmationModal } from '~/components/ConfirmationModal'
import { CopyableId } from '~/components/CopyableId'
import { Icon } from '~/components/Icon'
import { BaseInputElement, Input } from '~/components/Input'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { normalizeAsTrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { Modal } from '~/components/Modal'
import { PageContent } from '~/components/PageContent'
import { Select } from '~/components/Select'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { useToast } from '~/components/Toast/useToasts'
import { Tooltip } from '~/components/Tooltip'
import Table, { handleTableSearchAndSort, onColumnSort, TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { downloadCSVData } from '~/utils/fileDownload'
import { PbstckBidderSettingsFlyout } from './pbstck-bidder/PbstckBidderSettingsFlyout'

const toCurrencySymbol = (currency: CurrencyCode): CurrencySymbol => {
  return currencies[currency]
}

type NewAdminScope = Omit<AdminScope, 'id' | 'enabled'>

const CreateOrUpdateModal: FunctionComponent<{
  scope?: AdminScope
}> = ({ scope }) => {
  const { mutateAsync: createScope, isPending: isCreatingScope } = useCreateScope()
  const { mutateAsync: updateScope, isPending: isUpdatingScope } = useUpdateScope(scope?.id ?? '')

  const { close } = useGlobalModal()
  const currenciesT: SelectOptionProp[] = (Object.keys(currencies) as CurrencyCode[]).map((currencyCode) => ({
    value: currencyCode,
    label: `${currencyCode} - ${currencies[currencyCode]}`,
    trackingLabel: `common-admin-scopes-currency-${normalizeAsTrackingLabel(currencyCode)}-set`,
  }))

  const defaultValues: NewAdminScope = {
    name: '',
    currencyCode: 'EUR',
    pbjs: 'pbjs',
    features: ['analytics', 'abtest'],
    viewabilityEnabled: true,
    refreshEnabled: false,
    standaloneUserSessionEnabled: true,
    sessionTrackingDisabled: true,
    smartEnabled: false,
    coreWebVitalsEnabled: false,
    gamKeyNamesForPrebid: ['hb_pb', 'hb_deal', 'hbv_pb', 'bid_cpm'],
    timezone: 'Europe/Paris',
    mfaRequired: false,
    userAccessFeatures: [],
    ...scope,
  }
  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<AdminScope>({ defaultValues, mode: 'onChange' })

  const onSubmit: SubmitHandler<AdminScope> = async (newScope) => {
    if (isCreatingScope || isUpdatingScope) return
    await (scope ? updateScope(newScope) : createScope(newScope))
    close()
  }
  return (
    <Modal.Content>
      <Modal.Title>{scope ? 'Update scope' : 'New scope'}</Modal.Title>

      <Modal.Body>
        <form className="mt-2 flex flex-col gap-[34px]" id={'newScopeForm'} onSubmit={handleSubmit(onSubmit)}>
          <Input type={'text'} label={'Scope name'} control={control} name={'name'} error={errors.name?.message} rules={{ required: 'Please fill this to continue' }} />
          <Select control={control} name={'currencyCode'} options={currenciesT} label={'Scope currency'} searchable={true} />
          <Select
            control={control}
            name={'timezone'}
            label={'Timezone ID'}
            options={TIMEZONES.map((t): SelectOptionProp => ({ label: t, value: t, trackingLabel: `common-admin-scopes-timezone-${normalizeAsTrackingLabel(t)}-set` }))}
            error={errors.timezone?.message}
            rules={{ required: 'Please fill this to continue' }}
            searchable={true}
          />
          <Input type={'text'} control={control} name={'pbjs'} label={'pbjs variable'} />
          <Input type={'text'} control={control} name={'customerId'} label={'Customer ID'} />
        </form>
      </Modal.Body>

      <Modal.Actions>
        <Button variant={'tertiary'} onClick={close} trackingLabel={scope ? 'common-admin-scopes-edition-cancel' : 'common-admin-scopes-creation-cancel'}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          iconName={isCreatingScope || isUpdatingScope ? 'pubstack' : undefined}
          iconSpin={isCreatingScope || isUpdatingScope}
          disabled={isCreatingScope || isUpdatingScope}
          trackingLabel={scope ? 'common-admin-scopes-edition-confirm' : 'common-admin-scopes-creation-confirm'}
        >
          {scope ? (isUpdatingScope ? 'Upading...' : 'Update') : isCreatingScope ? 'Creating scope...' : 'Create scope'}
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}

const ArchiveScopeModal: FunctionComponent<{
  scope: AdminScope
}> = ({ scope }) => {
  const { mutateAsync: archiveScope, isPending: isArchivingScope } = useArchiveScope(scope.id, {
    successToast: `${scope.name} has been archived.`,
    errorToast: 'An error occurred while trying to archive the scope.',
  })

  const { close } = useGlobalModal()

  const handleArchiveClick = async () => {
    if (isArchivingScope) return

    await archiveScope(scope)
    close()
  }
  return (
    <Modal.Content>
      <Modal.Title>Archive scope</Modal.Title>

      <Modal.Body>
        You are about to archive <strong>{scope.name}</strong>. We will not collect data anymore. <br />
        Do you want to archive it?
      </Modal.Body>

      <Modal.Actions>
        <Button variant={'tertiary'} onClick={close} trackingLabel={'common-admin-scopes-archiving-cancel'}>
          Cancel
        </Button>
        <Button
          variant={'negative'}
          iconName={isArchivingScope ? 'pubstack' : undefined}
          iconSpin={isArchivingScope}
          disabled={isArchivingScope}
          onClick={handleArchiveClick}
          trackingLabel={'common-admin-scopes-archiving-confirm'}
        >
          {isArchivingScope ? 'Archiving...' : 'Archive'}
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}

const MarketplaceUpload: FunctionComponent<{
  onUploadMarketplace: (file: File) => void
}> = ({ onUploadMarketplace }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const toast = useToast()
  const handleFileChange: ChangeEventHandler<HTMLInputElement> = async (event) => {
    if (event.target.files) {
      const data = await event.target.files[0].text()
      try {
        JSON.parse(data)
        onUploadMarketplace(event.target.files[0])
      } catch (_e) {
        toast.alert('The JSON is not valid')
      }
      event.target.value = ''
    }
  }

  return (
    <>
      <Button variant={'secondary'} iconName={'upload'} onClick={() => inputRef.current?.click()} trackingLabel={'marketplace-admin-json-upload'}>
        Upload marketplace JSON
      </Button>
      <input style={{ display: 'none' }} onChange={handleFileChange} ref={inputRef} type={'file'} accept={'application/json'} />
    </>
  )
}

type ScopeTableRowProps = {
  scope: AdminScope
  onScopeRowClick: (scope: AdminScope) => void
}

const ScopeTableRow: FunctionComponent<ScopeTableRowProps> = ({ scope, onScopeRowClick }) => {
  const { mutateAsync: unarchive, isPending: isUnarchivingScope } = useUnarchiveScope(scope.id, {
    successToast: `${scope.name} has been unarchived.`,
    errorToast: 'An error occurred while trying to unarchive the scope.',
  })
  const { mutateAsync: deleteScope } = useDeleteScope(scope.id, {
    successToast: `${scope.name} has been permanently deleted.`,
    errorToast: 'An error occurred while trying to delete the scope.',
  })

  const modal = useGlobalModal()

  const onEdit = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.preventDefault()
      event.stopPropagation()
      modal.open(CreateOrUpdateModal, { scope })
    },
    [modal, scope]
  )

  const handleUnarchive = async () => {
    if (isUnarchivingScope) return

    await unarchive(scope)
  }

  return (
    <TableRow archived={!scope.enabled} clickable={{ onClick: () => onScopeRowClick(scope), trackingLabel: 'common-admin-scopes-detail-view' }}>
      <TableCell>
        {scope.name}
        <CopyableId id={scope.id} label={'Scope ID'} trackingLabel={'common-admin-scopes-id-copy'} className={'flex text-xs/[20px] font-normal text-slate-grey'} />
      </TableCell>
      <TableCell>{scope.customerId}</TableCell>
      <TableCell>
        {toCurrencySymbol(scope.currencyCode)} ({scope.currencyCode})
      </TableCell>
      <TableCell>{scope.timezone}</TableCell>
      <TableCell>{scope.pbjs}</TableCell>
      <TableCell>
        <div className="flex items-center gap-[5px]">
          <Tooltip title={'Viewability'}>
            <Icon name={'view'} fill={scope.viewabilityEnabled ? undefined : Colors.Silver} />
          </Tooltip>
          <Tooltip title={'Refresh'}>
            <Icon name={'refresh'} fill={scope.refreshEnabled ? undefined : Colors.Silver} />
          </Tooltip>
          <Tooltip title={'User Session'}>
            <Icon name={'group'} fill={scope.standaloneUserSessionEnabled ? undefined : Colors.Silver} />
          </Tooltip>
          <Tooltip title={'Smart'}>
            <Icon name={'smart'} className={cn(!scope.smartEnabled && '[--overrideColor:var(--color-silver)]')} width={Sizes['20']} />
          </Tooltip>
          <Tooltip title={'Core Web Vitals'}>
            <Icon name={'time'} fill={scope.coreWebVitalsEnabled ? undefined : Colors.Silver} />
          </Tooltip>
        </div>
      </TableCell>
      <TableCell>
        <div className="grid grid-cols-[repeat(3,40px)] [&_*~*]:ml-4">
          {scope.enabled ? (
            <>
              <Button iconName={'edit'} variant={'tertiary'} onClick={(e) => onEdit(e)} trackingLabel={'common-admin-scopes-edit'} />
              <Button
                iconName={'archive'}
                variant={'tertiary'}
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  modal.open(ArchiveScopeModal, { scope })
                }}
                trackingLabel={'common-admin-scopes-archive'}
              />
              <div />
            </>
          ) : (
            <>
              <div />
              <Button
                iconName={isUnarchivingScope ? 'pubstack' : 'unarchive'}
                iconSpin={isUnarchivingScope}
                disabled={isUnarchivingScope}
                variant={'tertiary'}
                onClick={async (event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  await handleUnarchive()
                }}
                trackingLabel={'common-admin-scopes-unarchive'}
              />
              <Tooltip title={'Permanently delete this scope'}>
                <Button
                  iconName={'delete'}
                  variant={'tertiary'}
                  className="[&_svg]:fill-cherry"
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    modal.open(ConfirmationModal, {
                      title: 'Delete scope permanently',
                      message: (
                        <>
                          <p>
                            You are about to <strong>permanently delete</strong> the scope <strong>{scope.name}</strong>.
                          </p>
                          <p>This action cannot be undone.</p>
                        </>
                      ),
                      trackingLabel: 'common-admin-scopes-deletion',
                      confirmVariant: 'negative',
                      onCancel: () => modal.close(),
                      onConfirm: async () => {
                        await deleteScope()
                        modal.close()
                      },
                    })
                  }}
                  trackingLabel={'common-admin-scopes-delete'}
                />
              </Tooltip>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  )
}

export const AdminScopePage = () => {
  // API hooks
  const { data: scopes, isLoading: isScopesLoading } = useAdminScopes()
  const [isScopesCSVLoading, setIsScopesCSVLoading] = useState(false)
  const usersCSVQuery = useAdminUsersCSV()
  const { mutateAsync: uploadMarketplaceSellers } = useUploadMarketplaceSellers({
    successToast: 'Sellers.json has been successfully uploaded',
    errorToast: 'An error occurred while trying to upload the file. Please contact the tech team',
  })

  // Hooks
  const navigate = useNavigate()
  const toast = useToast()
  const breadcrumbs = useBreadcrumbs()
  const user = useAuthenticatedUser()
  const modal = useGlobalModal()
  const flyout = useGlobalFlyout()

  // State
  const [search, setSearch] = useState('')
  const [columns, setColumns] = useState<TableColumns<AdminScope>>([
    {
      name: 'Name',
      isSortable: true,
      order: 'ascending',
      attributeSort: 'name',
    },
    {
      name: 'Customer ID',
      isSortable: true,
      order: 'none',
      attributeSort: 'customerId',
      sort: (a, b) => compareNames(a.customerId ?? '', b.customerId ?? ''),
    },
    {
      name: 'Currency',
      isSortable: true,
      order: 'none',
      attributeSort: 'currencyCode',
    },
    {
      name: 'Timezone',
      isSortable: true,
      order: 'none',
      attributeSort: 'timezone',
    },
    {
      name: 'Prebid var',
      isSortable: true,
      order: 'none',
      attributeSort: 'pbjs',
    },
    {
      name: 'Data Ingestion',
      isSortable: false,
    },
    {
      name: 'Action',
      isSortable: false,
    },
  ])

  async function downloadScopeCSV() {
    setIsScopesCSVLoading(true)
    try {
      const response = await api.get('/admin/scopes/csv', { responseType: 'text' })
      downloadCSVData(response.data, 'admin-scopes')
    } catch {
      toast.alert('An error occurred while downloading the CSV')
    } finally {
      setIsScopesCSVLoading(false)
    }
  }

  async function downloadUserCSV() {
    const result = await usersCSVQuery.fetch()
    const users = result.data
    if (!users) return
    const csvContent =
      'Email,UserName,LastLogin,ScopeName\n' +
      users
        .map((u: AdminCSVUser) =>
          [
            u.email,
            u.name,
            u.lastLogin,
            u.scopesWithRoles === 'all'
              ? ['all']
              : u.scopesWithRoles
                  .map(
                    (scopeWithRole) =>
                      `${scopes?.find((s) => s.id === scopeWithRole.scopeId)?.name ?? scopeWithRole.scopeId}(${scopeWithRole.role} ${scopeWithRole.alertEmailEnabled ? 'alertEmailEnabled' : ''})`
                  )
                  .join(' / '),
          ].join(',')
        )
        .join('\n')

    downloadCSVData(csvContent, 'admin-users')
  }

  async function uploadMarketplaceJSON(json: File) {
    const data = new FormData()
    data.append('sellers', json)
    await uploadMarketplaceSellers(data)
  }

  const sortAndGroup = (scopesList: AdminScope[]) => handleTableSearchAndSort(columns, scopesList, search, ['name'])
  const groupedScopes = useMemo(
    () =>
      [...(scopes || [])].reduce(
        (acc, scope) => ({
          ...acc,
          [`${scope.enabled}`]: [...(acc[`${scope.enabled}`] || []), scope],
        }),
        {} as { [key: string]: AdminScope[] }
      ),
    [scopes]
  )
  const displayedScopes = useMemo(() => [...sortAndGroup(groupedScopes['true'] || []), ...sortAndGroup(groupedScopes['false'] || [])], [sortAndGroup, groupedScopes])

  const openCreateOrUpdateScopeModal = (scope?: AdminScope) => {
    modal.open(CreateOrUpdateModal, { scope })
  }

  const openPbstckBidderSettings = useCallback(() => {
    flyout.open(PbstckBidderSettingsFlyout, { color: Colors.Kaiminus, closeTrackingLabel: 'common-admin-spark-config-view-close' }, { onClose: () => flyout.close() })
  }, [flyout])

  const onScopeRowClick = (scope: AdminScope) => navigate(`/admin/scopes/${scope.id}`)

  return (
    <PageContent
      breadcrumbs={breadcrumbs}
      headerActions={
        <>
          <Button variant="secondary" onClick={openPbstckBidderSettings} iconName="settings" trackingLabel={'common-admin-spark-config-view'}>
            Spark settings
          </Button>
          <MarketplaceUpload onUploadMarketplace={uploadMarketplaceJSON} />
          <Button
            variant={'secondary'}
            onClick={downloadUserCSV}
            disabled={usersCSVQuery.isFetching}
            iconSpin={usersCSVQuery.isFetching}
            iconName={usersCSVQuery.isFetching ? 'pubstack' : 'download'}
            trackingLabel={'common-admin-users-download'}
          >
            Download CSV user
          </Button>
          <Button
            variant={'secondary'}
            onClick={downloadScopeCSV}
            disabled={isScopesCSVLoading}
            iconSpin={isScopesCSVLoading}
            iconName={isScopesCSVLoading ? 'pubstack' : 'download'}
            trackingLabel={'common-admin-scopes-download'}
          >
            Download CSV scopes
          </Button>
          {user.isTechProductRole && (
            <>
              <Button variant={'secondary'} iconName={'shipping'} onClick={() => navigate('/admin/delivery')} trackingLabel={'common-admin-delivery-navigate'}>
                Delivery
              </Button>
              <Button variant={'secondary'} iconName={'snooze'} onClick={() => navigate('/admin/snooze-alarm')} trackingLabel={'common-admin-snooze-alarm-navigate'}>
                Snooze Alarm
              </Button>
            </>
          )}
        </>
      }
    >
      <div className="my-4 flex content-center items-center">
        <BaseInputElement className="w-[490px]" type={'text'} iconLeft={'search'} labelIsPlaceholder label={'Search by name...'} onChange={(event) => setSearch(event.target.value)} />
        <Button className="ml-5 flex-[0_1_auto]" variant={'primary'} onClick={() => openCreateOrUpdateScopeModal()} trackingLabel={'common-admin-scopes-new'}>
          New scope
        </Button>
      </div>

      <Table columns={columns} isLoading={isScopesLoading} onClickHeading={(column) => onColumnSort(columns, column, setColumns)}>
        {displayedScopes.map((scope) => (
          <ScopeTableRow key={scope.id} scope={scope} onScopeRowClick={onScopeRowClick} />
        ))}
      </Table>
    </PageContent>
  )
}
